// src/components/auth/LoginForm.tsx
"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Eye, EyeOff, Loader2, AlertTriangle, Info } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { handleAuthError } from "@/lib/auth-error-handler";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" }),
});

type FormValues = z.infer<typeof formSchema>;

export function LoginForm() {
  const { signIn } = useAuth();
  const { toast } = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [loginAttempts, setLoginAttempts] = useState(0);
  const redirectUrl = searchParams?.get("redirect") || "/dashboard";

  // Check for URL params that might indicate special states
  useEffect(() => {
    const errorParam = searchParams?.get("error");
    const messageParam = searchParams?.get("message");
    const sessionExpired = searchParams?.get("expired") === "true";

    if (errorParam) {
      setErrorMessage(decodeURIComponent(errorParam));
    } else if (sessionExpired) {
      setErrorMessage("Your session has expired. Please sign in again.");
    }

    if (messageParam) {
      setInfoMessage(decodeURIComponent(messageParam));
    }
  }, [searchParams]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    // Clear previous error messages
    setErrorMessage("");
    setIsLoading(true);

    try {
      const { error } = await signIn(data.email, data.password);

      if (error) {
        // Increment login attempts
        setLoginAttempts((prev) => prev + 1);

        // Get user-friendly error message
        const errorMsg = handleAuthError(error);
        setErrorMessage(errorMsg);

        // Show toast for too many attempts
        if (loginAttempts >= 2) {
          toast({
            variant: "destructive",
            title: "Multiple failed attempts",
            description:
              "Too many failed login attempts. You can try resetting your password.",
          });
        }
        return;
      }

      // Successful login
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });

      // Redirect to the requested page or dashboard
      router.push(redirectUrl);
    } catch (error) {
      const errorMsg = handleAuthError(error);
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-6 shadow-sm">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-bold">Welcome to Sonar Finance</h1>
        <p className="text-sm text-muted-foreground">
          Enter your credentials to access your account
        </p>
      </div>

      {/* Error alert */}
      {errorMessage && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Authentication Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* Info alert */}
      {infoMessage && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Information</AlertTitle>
          <AlertDescription>{infoMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          noValidate
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="you@example.com"
                    type="email"
                    autoComplete="email"
                    disabled={isLoading}
                    autoFocus
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder="••••••••"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      disabled={isLoading}
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={togglePasswordVisibility}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="sr-only">
                        {showPassword ? "Hide password" : "Show password"}
                      </span>
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            aria-label="Sign in to your account"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>
      </Form>

      <div className="mt-4 text-center text-sm">
        <Button
          variant="link"
          className="px-0 text-sm font-normal"
          onClick={() => router.push("/forgot-password")}
          disabled={isLoading}
        >
          Forgot your password?
        </Button>
      </div>

      <div className="relative flex items-center justify-center py-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative bg-card px-4 text-sm text-muted-foreground">
          Don't have an account?
        </div>
      </div>

      <Button
        variant="outline"
        className="w-full"
        onClick={() => router.push("/signup")}
        disabled={isLoading}
      >
        Create an account
      </Button>
    </div>
  );
}

// If you want to add social login later, you can create a SocialLoginButtons component:
/*
export function SocialLoginButtons() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  // Import from auth.ts
  const { signInWithOAuth } = useAuth();
  
  const handleSocialLogin = async (provider: 'google' | 'github' | 'apple') => {
    setIsLoading(true);
    try {
      await signInWithOAuth(provider);
      // Note: No success toast here, as the page will redirect to the OAuth provider
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Authentication failed",
        description: "Could not sign in with social provider.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="flex flex-col space-y-2">
      <Button
        variant="outline"
        className="w-full"
        onClick={() => handleSocialLogin('google')}
        disabled={isLoading}
      >
        <svg className="mr-2 h-4 w-4" /* Google SVG icon here *\/ />
        Continue with Google
      </Button>
      
      <Button
        variant="outline"
        className="w-full"
        onClick={() => handleSocialLogin('github')}
        disabled={isLoading}
      >
        <svg className="mr-2 h-4 w-4" /* GitHub SVG icon here *\/ />
        Continue with GitHub
      </Button>
    </div>
  );
}
*/
